import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  convertEstimateToInvoiceApi,
  fetchBusinessEstimates,
  putRawBusinessEstimateApi,
} from '~/api';
import { INVOICE_LIST_ROUTE } from '~/constants/routes';
import { useBoolean } from '~/hooks/useBoolean';
import { useUserLimits } from '~/hooks/useUserLimits';
import { useSelector } from '~/store';
import { hideLoader, showLoader } from '~/store/slices/app';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { IListEstimate } from '~/types/estimate';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';

interface EstimateApiResponse {
  next: string | null;
  previous: string | null;
  count: number;
  total_pages: number;
  results: IListEstimate[];
}

interface State {
  listIds: string[];
  listData: Record<string, IListEstimate>;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

const initialState: State = {
  listIds: [],
  listData: {},
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { estimates: EstimateApiResponse; page: number } }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'RESET_LIST' }
  | { type: 'UPDATE_DATA'; payload: { estimateUuid: string; data: Partial<IListEstimate> } };

const estimateReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      const { estimates, page } = action.payload;
      const newIds = estimates.results.map(estimate => estimate.estimate_uuid);
      const newData = estimates.results.reduce<Record<string, IListEstimate>>((acc, estimate) => {
        acc[estimate.estimate_uuid] = estimate;
        return acc;
      }, {});

      return {
        ...state,
        isLoading: false,
        listIds: newIds,
        listData: newData,
        currentPage: page,
        totalPages: estimates.total_pages,
        totalCount: estimates.count,
      };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'RESET_LIST':
      return { ...initialState };

    case 'UPDATE_DATA': {
      const { estimateUuid, data } = action.payload;
      return {
        ...state,
        listData: {
          ...state.listData,
          [estimateUuid]: {
            ...state.listData[estimateUuid],
            ...data,
          },
        },
      };
    }

    default:
      return state;
  }
};

export const useEstimateList = () => {
  const [state, dispatch] = useReducer(estimateReducer, initialState);
  const [changeToggle, changeToggleActions] = useBoolean();
  const [deleteEstimateId, setDeleteEstimateId] = useState<null | string>(null);
  const [viewEstimateId, setViewEstimateId] = useState<null | string>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const businessUuid = useSelector(selectBusinessUuid);
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();

  // Capture viewId from URL on mount and strip it immediately
  const pendingViewId = useRef<string | null>(null);
  useEffect(() => {
    const viewId = searchParams.get('viewId');
    if (viewId) {
      pendingViewId.current = viewId;
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete('viewId');
      setSearchParams(cleaned, { replace: true });
    }
  }, []);

  // Use user limits hook
  const { canCreateEstimate, refetch: refetchUserLimits } = useUserLimits(true);

  const fetchEstimates = useCallback(
    async (page = 1, queryParams: string) => {
      dispatch({ type: 'FETCH_START' });

      const promise = fetchBusinessEstimates(businessUuid, queryParams, page);
      const response = await apiResponseHandler<EstimateApiResponse>(promise);

      if (response.type === ApiResponseEnum.Success) {
        dispatch({ type: 'FETCH_SUCCESS', payload: { estimates: response.data, page } });
        // Open detail modal if we have a pending viewId from URL
        if (pendingViewId.current) {
          setViewEstimateId(pendingViewId.current);
          pendingViewId.current = null;
        }
      } else {
        dispatch({ type: 'FETCH_FAILURE', error: response.error });
      }
    },
    [],
  );

  async function onEditListEstimate(estimateUuid: string, estimateUpdates: Partial<IListEstimate>) {
    const promise = await putRawBusinessEstimateApi(businessUuid, estimateUuid, estimateUpdates);
    const response = await apiResponseHandler<IListEstimate>(promise);
    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'UPDATE_DATA', payload: { estimateUuid, data: response.data } });
      toast.success('Successfully updated estimate');
      changeToggleActions.toggle();
    } else {
      toast.error('An error occurred while saving your data, please try again later');
      return;
    }
  }

  async function convertEstimateToInvoice(estimateUuid: string) {
    reduxDispatch(showLoader());
    const promise = await convertEstimateToInvoiceApi(businessUuid, estimateUuid);
    const response = await apiResponseHandler<IListEstimate>(promise);
    if (response.type === ApiResponseEnum.Success) {
      toast.success('Successfully created invoice');
      refetchUserLimits();
      changeToggleActions.toggle();
      reduxDispatch(hideLoader());
      navigate(INVOICE_LIST_ROUTE);
    } else {
      toast.error(response.error);
      reduxDispatch(hideLoader());
      return;
    }
  }

  const handleStatusChange = (qsKey: string, selectedValue: string) => {
    dispatch({ type: 'RESET_LIST' });
    const newParams = new URLSearchParams(searchParams);
    if (selectedValue) {
      newParams.set(qsKey, selectedValue);
    } else {
      newParams.delete(qsKey);
    }
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchEstimates(state.currentPage, `?${searchParams.toString()}`);
  }, [fetchEstimates, deleteEstimateId, searchParams, changeToggle]);

  return {
    ...state,
    deleteEstimateId,
    setDeleteEstimateId,
    onEditListEstimate,
    viewEstimateId,
    setViewEstimateId,
    handleStatusChange,
    canCreateEstimate,
    convertEstimateToInvoice,
    fetchNextPage: () => {
      if (state.currentPage < state.totalPages) {
        fetchEstimates(state.currentPage + 1, `?${searchParams.toString()}`);
      }
    },
    fetchSpecificPage: (page: number) => {
      if (page >= 1 && page <= state.totalPages) {
        fetchEstimates(page, `?${searchParams.toString()}`);
      }
    },
    resetList: () => dispatch({ type: 'RESET_LIST' }),
  };
};
